export const PRIORITY_OPTIONS = ["high", "medium", "low"];

export const sanitizeNoticeText = (value) =>
    String(value || "")
        .replace(/\s+/g, " ")
        .trim();

export const htmlToPlainText = (html = "") => {
    if (!html) return "";
    return String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

export const getSafeNoticeUrl = (url = "") => {
    const trimmed = String(url || "").trim();
    if (!trimmed) return "";
    try {
        const parsed = new URL(trimmed);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return "";
        }
        return parsed.toString();
    } catch (error) {
        return "";
    }
};

export const validateNoticeDraft = (draft) => {
    const errors = {};
    const title = sanitizeNoticeText(draft?.title);
    const contentText = htmlToPlainText(draft?.contentHtml);
    const priority = String(draft?.priority || "").toLowerCase();
    const linkUrl = getSafeNoticeUrl(draft?.linkUrl);
    if (title.length < 3) {
        errors.title = "Title must be at least 3 characters";
    }
    if (contentText.length < 10) {
        errors.content = "Notice content must be at least 10 characters";
    }
    if (!PRIORITY_OPTIONS.includes(priority)) {
        errors.priority = "Priority must be high, medium, or low";
    }
    if (!linkUrl) {
        errors.linkUrl = "A valid external URL is required";
    }
    const startDate = draft?.startAt ? new Date(draft.startAt) : null;
    const expiryDate = draft?.expiryAt ? new Date(draft.expiryAt) : null;
    if (startDate && Number.isNaN(startDate.getTime())) {
        errors.startAt = "Start date is invalid";
    }
    if (expiryDate && Number.isNaN(expiryDate.getTime())) {
        errors.expiryAt = "Expiry date is invalid";
    }
    if (startDate && expiryDate && expiryDate <= startDate) {
        errors.expiryAt = "Expiry date must be after start date";
    }
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

export const toSafeDate = (value) => {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isNoticeActive = (notice, now = new Date()) => {
    if (!notice?.isVisible) return false;
    const startAt = toSafeDate(notice.startAt);
    const expiryAt = toSafeDate(notice.expiryAt);
    if (startAt && startAt > now) return false;
    if (expiryAt && expiryAt < now) return false;
    return true;
};

export const filterNotices = (notices = [], options = {}) => {
    const query = String(options.query || "").toLowerCase().trim();
    const priority = String(options.priority || "").toLowerCase();
    const startDate = options.startDate ? new Date(options.startDate) : null;
    const endDate = options.endDate ? new Date(options.endDate) : null;
    return notices.filter((notice) => {
        const title = String(notice.title || "").toLowerCase();
        const text = String(notice.contentText || htmlToPlainText(notice.contentHtml)).toLowerCase();
        const priorityMatch = !priority || priority === "all" || String(notice.priority || "").toLowerCase() === priority;
        const searchMatch = !query || title.includes(query) || text.includes(query);
        const createdAt = toSafeDate(notice.createdAt);
        const startMatch = !startDate || (createdAt && createdAt >= startDate);
        const endMatch = !endDate || (createdAt && createdAt <= endDate);
        return priorityMatch && searchMatch && startMatch && endMatch;
    });
};

export const paginateItems = (items = [], page = 1, pageSize = 8) => {
    const safePageSize = Math.max(1, Number(pageSize) || 1);
    const totalPages = Math.max(1, Math.ceil(items.length / safePageSize));
    const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
    const startIndex = (safePage - 1) * safePageSize;
    return {
        totalPages,
        page: safePage,
        pageSize: safePageSize,
        items: items.slice(startIndex, startIndex + safePageSize)
    };
};
